/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactNode, useMemo } from 'react';
import type { ComponentType } from '@/components/region';
import { Component as RegionComponent } from '@/components/region/component';
import { RegionWrapper } from '@/components/region/region-wrapper';

export interface RegionItem {
    key: string;
    content: ReactNode;
}

/** Stable empty-array reference so a `fallback` default never changes identity between renders. */
export const NO_REGION_ITEMS: RegionItem[] = [];

/**
 * Clone a PD component with extra props merged into its `data` (which <Component> spreads onto the
 * rendered component), so a layout can pass props such as image priority to its children.
 */
export function withComponentData(comp: ComponentType, extra: Record<string, unknown>): ComponentType {
    const data = (comp.data as Record<string, unknown> | undefined) ?? {};
    // SCAPI types Component.data as Record<string, never>; cast through unknown to attach the props.
    return { ...comp, data: { ...data, ...extra } } as unknown as ComponentType;
}

interface UseRegionItemsOptions {
    /** Parent component data from Page Designer. */
    component?: ComponentType;
    regionId: string;
    /** Plain items (Storybook/test path). Used only when the region has no components. */
    fallback?: RegionItem[];
    /** Optional per-item transform, e.g. `withComponentData`. Must be referentially stable. */
    decorate?: (comp: ComponentType, index: number) => ComponentType;
    /** Class for each rendered region component. */
    itemClassName?: string;
}

interface RegionItems {
    items: RegionItem[];
    /**
     * Wraps the rendered items in the PD RegionWrapper, which maps each authored component to its
     * region and keeps it selectable in the editor. Adds no DOM on the storefront.
     */
    wrap: (children: ReactNode) => ReactNode;
}

/**
 * Turns the components authored in a Page Designer region into renderable items. Shared by the
 * PriceShoes layout components (Carousel and similar) so each one only owns its own layout.
 */
export function useRegionItems({
    component,
    regionId,
    fallback = NO_REGION_ITEMS,
    decorate,
    itemClassName,
}: UseRegionItemsOptions): RegionItems {
    const region = useMemo(
        () => (Array.isArray(component?.regions) ? component.regions.find((r) => r.id === regionId) : undefined),
        [component, regionId]
    );
    const regionMetadata = useMemo(
        () => component?.designMetadata?.regionDefinitions?.find((r) => r.id === regionId),
        [component, regionId]
    );

    const items = useMemo<RegionItem[]>(() => {
        const regionComponents = Array.isArray(region?.components)
            ? (region.components.filter((comp) => comp.id && comp.typeId) as ComponentType[])
            : [];
        if (regionComponents.length === 0) return fallback;
        return regionComponents.map((comp, index) => ({
            key: comp.contentLinkUuid ?? comp.id,
            content: (
                <RegionComponent
                    component={decorate ? decorate(comp, index) : comp}
                    regionId={regionId}
                    className={itemClassName}
                />
            ),
        }));
    }, [region, regionId, fallback, decorate, itemClassName]);

    const wrap = (children: ReactNode): ReactNode =>
        region ? (
            <RegionWrapper
                region={region}
                designMetadata={{
                    id: regionId,
                    componentTypeInclusions: regionMetadata?.componentTypeInclusions ?? [],
                    componentTypeExclusions: regionMetadata?.componentTypeExclusions ?? [],
                }}>
                {children}
            </RegionWrapper>
        ) : (
            children
        );

    return { items, wrap };
}
